import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import cx from 'classnames';
import { BlockTypes } from '../common/constants';
import { clamp } from '../common/utilities';
import { useAutomaticFontSizing } from '../lib/useAutomaticFontSizing';
import { useDebounce } from '../lib/useDebounce';

const useStyles = makeStyles({
  wrapper: {
    fontFamily: 'courier',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    border: `1px solid ${colors.blue[800]}`,
    borderRadius: '2px',
    boxShadow:
      'inset 0 -1.5px 0 0 #E1E7ED, 0 6px 10px 0 rgba(0,32,64,0.07), 0 1px 18px 0 rgba(0,32,64,0.06), 0 3px 5px -1px rgba(0,32,64,0.07)',
    transition: 'border-color 0.1s',
    padding: 1,
    resize: 'none',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
    whiteSpace: 'break-spaces',
    textAlign: 'left',
    lineHeight: 1,
    '&:hover, &:focus': {
      boxShadow:
        'inset 0 -1.5px 0 0 #DCEEFF, 0 6px 10px 0 rgba(0,89,218,0.07), 0 1px 18px 0 rgba(0,89,218,0.06), 0 3px 5px -1px rgba(0,89,218,0.07)',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  focused: {
    border: '2px solid #D20004',
  },
});

interface IProps {
  blockId: string;
  value?: string;
  fontSize: number;
  height: number;
  width: number;
  updateBlock: (updates: { fontSize?: number; value?: string; blockType?: string }) => void;
  signingExperienceComplete: boolean;
  required: boolean;
  focused: boolean;
  tabIndex: number;
  tabToBlock: (tabIndex: number) => void;
  pageDimensions: { width?: number; height?: number };
  pageScale: number;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const TextBox = (props: IProps) => {
  const classes = useStyles();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const { fontSize, value, setValue } = useAutomaticFontSizing({
    height: props.height,
    width: props.width,
    initialFontSize: props.fontSize,
    initialValue: props.value,
    fontFamily: 'courier',
    multiline: true,
    blockType: 'Text',
  });
  const finalFontSize = clamp(fontSize * props.pageScale, { min: 6, max: 24 });

  const debouncedFontsize = useDebounce(fontSize, 200);
  const debouncedValue = useDebounce(value, 200);

  React.useEffect(() => {
    props.updateBlock({ fontSize, value, blockType: BlockTypes.TEXT });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFontsize, debouncedValue]);

  React.useEffect(() => {
    setValue(props.value || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.blockId]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const onType = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const preventEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleFocus = (_e: any) => {
    props.tabToBlock(props.tabIndex);
  };

  return (
    <textarea
      ref={inputRef}
      className={
        props.signingExperienceComplete && value === '' && props.required && props.focused
          ? cx(classes.wrapper, classes.focused)
          : classes.wrapper
      }
      onChange={onType}
      onKeyDown={preventEnter}
      onMouseDown={handleFocus}
      style={{
        height: `100%`,
        width: `100%`,
        fontSize: `${finalFontSize}px`,
      }}
      value={value}
    />
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default TextBox;
